import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { expenseApi } from '../api/accountsApi';
import { Expense } from '../pages/expenses/expense.types';

export interface FinanceDashboardData {
  expenses: Expense[];
}

// Fetch dashboard data
const fetchFinanceDashboardData = async (): Promise<FinanceDashboardData | null> => {
  try {
    const expensesResponse = await expenseApi.fetchAll();
    console.log('Finance Dashboard fetch responses:', expensesResponse.data);
    return {
      expenses: expensesResponse.data || [], // Ensure we return an array
    };
  } catch (error) {
    console.error('Error fetching finance dashboard data:', error);
    return null;
  }
};

export const useFinanceDashboard = () => {
  const [dashboardData, setDashboardData] = useState<FinanceDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchFinanceDashboardData();
        if (data) {
          setDashboardData(data);
        } else {
          setError('Failed to load finance dashboard data');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Memoized calculations to prevent unnecessary recalculations
  const metrics = useMemo(() => {
    if (!dashboardData?.expenses) return null;

    const { expenses } = dashboardData;
    const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    // Time-based calculations
    const last3MonthsExpenses = expenses.filter(expense => 
      dayjs(expense.date).isAfter(dayjs().subtract(3, 'months'))
    );
    const last6MonthsExpenses = expenses.filter(expense => 
      dayjs(expense.date).isAfter(dayjs().subtract(6, 'months'))
    );
    const last9MonthsExpenses = expenses.filter(expense => 
      dayjs(expense.date).isAfter(dayjs().subtract(9, 'months'))
    );

    return {
      totalAmount,
      last3MonthsAmount: last3MonthsExpenses.reduce((sum, expense) => sum + expense.amount, 0),
      last6MonthsAmount: last6MonthsExpenses.reduce((sum, expense) => sum + expense.amount, 0),
      last9MonthsAmount: last9MonthsExpenses.reduce((sum, expense) => sum + expense.amount, 0),
      totalExpenses: expenses.length,
    };
  }, [dashboardData]);

  // Memoized category data
  const categoryData = useMemo(() => {
    if (!dashboardData?.expenses) return null;

    const { expenses } = dashboardData;
    const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    const categoryTotals = expenses.reduce((acc, expense) => {
      const category = expense.catL1 || 'Uncategorized';
      acc[category] = (acc[category] || 0) + expense.amount;
      return acc;
    }, {} as Record<string, number>);

    const top3Categories = Object.entries(categoryTotals)
      .map(([name, amount]) => ({
        name,
        amount,
        percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 3);

    return {
      top3Categories,
      totalAmount,
    };
  }, [dashboardData]);

  // Memoized year data
  const yearData = useMemo(() => {
    if (!dashboardData?.expenses) return [];

    const { expenses } = dashboardData;
    const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    const yearTotals = expenses.reduce((acc, expense) => {
      const year = dayjs(expense.date).year();
      acc[year] = (acc[year] || 0) + expense.amount;
      return acc;
    }, {} as Record<number, number>);

    return Object.entries(yearTotals)
      .map(([year, amount]) => ({
        year: parseInt(year),
        amount,
        percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
      }))
      .sort((a, b) => b.year - a.year);
  }, [dashboardData]);

  // Memoized subcategory data
  const subcategoryData = useMemo(() => {
    if (!dashboardData?.expenses) return null;

    const { expenses } = dashboardData;
    const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    const getSubcategories = (category: string) => {
      const categoryExpenses = expenses.filter(expense => expense.catL1 === category);
      const subcategoryTotals = categoryExpenses.reduce((acc, expense) => {
        const subcategory = expense.catL2 || 'Uncategorized';
        acc[subcategory] = (acc[subcategory] || 0) + expense.amount;
        return acc;
      }, {} as Record<string, number>);

      return Object.entries(subcategoryTotals)
        .map(([name, amount]) => ({
          name,
          amount,
          percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
        }))
        .sort((a, b) => b.amount - a.amount);
    };

    return {
      nakipurLand: getSubcategories('Nakipur Land'),
      project: getSubcategories('Project'),
      software: getSubcategories('Software'),
    };
  }, [dashboardData]);

  // Memoized L3L4 data
  const l3l4Data = useMemo(() => {
    if (!dashboardData?.expenses) return [];

    const { expenses } = dashboardData;
    const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    const l3l4Totals = expenses.reduce((acc, expense) => {
      const l3Key = expense.catL3 || 'Uncategorized L3';
      const l4Key = expense.catL4 || 'Uncategorized L4';
      
      if (!acc[l3Key]) {
        acc[l3Key] = { 
          amount: 0, 
          catL1: expense.catL1 || '', 
          catL2: expense.catL2 || '', 
          catL3: expense.catL3 || '', 
          catL4: expense.catL4 || '' 
        };
      }
      acc[l3Key].amount += expense.amount;
      
      if (expense.catL4) {
        if (!acc[l4Key]) {
          acc[l4Key] = { 
            amount: 0, 
            catL1: expense.catL1 || '', 
            catL2: expense.catL2 || '', 
            catL3: expense.catL3 || '', 
            catL4: expense.catL4 || '' 
          };
        }
        acc[l4Key].amount += expense.amount;
      }
      
      return acc;
    }, {} as Record<string, { amount: number; catL1: string; catL2: string; catL3: string; catL4: string }>);

    return Object.entries(l3l4Totals)
      .map(([name, data]) => ({
        name,
        amount: data.amount,
        percentage: totalAmount > 0 ? (data.amount / totalAmount) * 100 : 0,
        link: `/finance/expenses?catL3=[${data.catL3},equals]&catL4=[${data.catL4},equals]`
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [dashboardData]);

  return {
    dashboardData,
    loading,
    error,
    metrics,
    categoryData,
    yearData,
    subcategoryData,
    l3l4Data,
    refetch: () => {
      setLoading(true);
      setError(null);
      fetchFinanceDashboardData().then(data => {
        if (data) setDashboardData(data);
        setLoading(false);
      }).catch(err => {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      });
    }
  };
};
